import React, { forwardRef, HTMLProps, Ref, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DraggableProvidedDraggableProps, DraggableProvidedDragHandleProps } from 'react-beautiful-dnd';
import { Button, ButtonProps } from 'components/_new/Button';
import { SurveyProgramQuestionBase, SurveyQuestionType } from 'types/Survey';
import { Box, BoxSection } from 'components/_new/Box';
import { Tag } from 'components/_new/Tag';
import { ButtonGroup } from 'components/_new/ButtonGroup/ButtonGroup';
import { classNames } from 'primereact/utils';
import { Icon } from 'components/_new/Icon';
import { ApplicationRoleEnum } from 'types/Application';
import { ArrowList } from 'components/_new/List';
import { getQuestionTypeOptions } from './utils';

const OPTIONS_LIMIT = 5;

type MoveButtonProps = Pick<ButtonProps, 'icon' | 'iconStyle' | 'iconClassName' | 'className' | 'disabled'>;

type QuestionItemProps = {
	no: number | false;
	question: SurveyProgramQuestionBase;
	dragHandleProps?: DraggableProvidedDragHandleProps | null | false;
	isDragging?: boolean;
	onEdit?: (questionId: number) => void;
	onDelete?: (questionId: number) => void;
	onMove?: (questionId: number, direction: 'up' | 'down') => void;
	disableActions?: boolean;
	moveProps?: {
		up?: MoveButtonProps | false;
		down?: MoveButtonProps | false;
	};
} & Pick<HTMLProps<HTMLDivElement>, 'style' | 'className'> &
	Partial<DraggableProvidedDraggableProps>;

export const QuestionItem = forwardRef((props: QuestionItemProps, ref: Ref<HTMLDivElement>) => {
	const {
		no,
		question,
		dragHandleProps,
		isDragging = false,
		onEdit,
		onDelete,
		onMove,
		disableActions = false,
		moveProps,
		...restProps
	} = props;
	const { t } = useTranslation();
	const questionTypeOptions = getQuestionTypeOptions(t);

	const [showAllOptions, setShowAllOptions] = useState(false);

	// Buduj tablicę przycisków akcji
	const buttons: any[] = [
		{
			key: 'edit',
			label: t('actions.edit'),
			icon: 'pencil',
			iconClassName: 'text-purple-500',
			onClick: () => onEdit?.(question.id),
			disabled: Boolean(question.programId === null),
		},
	];

	if (moveProps?.up !== false) {
		buttons.push({
			key: 'move-up',
			label: t('actions.moveToUp'),
			icon: moveProps?.up?.icon || 'up-long',
			iconStyle: moveProps?.up?.iconStyle || 'solid',
			className: moveProps?.up?.className,
			iconClassName: moveProps?.up?.iconClassName,
			iconOnly: true,
			onClick: () => onMove?.(question.id, 'up'),
			disabled: moveProps?.up?.disabled || false,
		});
	}

	if (moveProps?.down !== false) {
		buttons.push({
			key: 'move-down',
			label: t('actions.moveToDown'),
			icon: moveProps?.down?.icon || 'down-long',
			iconStyle: moveProps?.down?.iconStyle || 'solid',
			className: moveProps?.down?.className,
			iconClassName: moveProps?.down?.iconClassName,
			iconOnly: true,
			onClick: () => onMove?.(question.id, 'down'),
			disabled: moveProps?.down?.disabled || false,
		});
	}

	if (question.programId) {
		buttons.push({
			key: 'remove',
			label: t('actions.remove'),
			icon: 'trash',
			iconClassName: 'text-danger',
			onClick: () => onDelete?.(question.id),
		});
	} else {
		buttons.push({
			key: 'disable',
			label: question.isExcluded ? t('actions.turnOn') : t('actions.turnOff'),
			icon: question.isExcluded ? 'eye' : 'eye-slash',
			iconClassName: question.isExcluded ? 'text-primary' : 'text-danger',
			onClick: () => onDelete?.(question.id),
		});
	}

	return (
		<Box
			ref={ref}
			variant="white-bordered"
			dropShadow={isDragging}
			dragHandleProps={dragHandleProps === false ? null : dragHandleProps}
			{...restProps}
		>
			<BoxSection
				headerClassName="flex flex-column gap-1"
				header={
					<>
						<div className="flex flex-column xl:flex-row gap-2 align-items-start justify-content-between">
							<div className="flex flex-row gap-2 flex-wrap justify-content-start align-items-center">
								{no !== false && <Tag variant="blue">Nr. {no}</Tag>}
								{question.isExcluded && (
									<Tag variant="grey" min>
										{t('programPanel.feedback.tag.excluded')}
									</Tag>
								)}
								{!question.programId && (
									<Tag variant="rose" min>
										{t('programPanel.feedback.tag.systemQuestion')}
									</Tag>
								)}
								{!question.isExcluded && question.required && (
									<Tag variant="danger" min>
										{t('programPanel.feedback.tag.required')}
									</Tag>
								)}

								{question.forApplicationRole === ApplicationRoleEnum.MENTOR && (
									<span
										title={t('knowledgeBase.programPanel.article.mentorAccess')}
										className="text-purple-500 text-sm"
									>
										<Icon name="user-tie" className="mr-2" />
										<span>{t('knowledgeBase.programPanel.article.mentorAccess')}</span>
									</span>
								)}
								{question.forApplicationRole === ApplicationRoleEnum.MENTEE && (
									<span
										title={t('knowledgeBase.programPanel.article.menteeAccess')}
										className="text-blue-500 text-sm"
									>
										<Icon name="user-graduate" className="mr-2" />
										<span>{t('knowledgeBase.programPanel.article.menteeAccess')}</span>
									</span>
								)}
							</div>
							<div className="flex flex-row gap-2">
								<ButtonGroup
									variant="primary-text"
									variantSize="sm"
									disabled={disableActions}
									buttons={buttons}
								/>
							</div>
						</div>
						<div className="flex-1">
							<div className={classNames({ 'text-gray': question.isExcluded })}>{question.text}</div>
						</div>
					</>
				}
			>
				<div className="flex flex-column gap-2">
					<div className="flex flex-row gap-2">
						<span className="font-semibold">{t('programPanel.feedback.type')}:</span>
						<span>{questionTypeOptions[question.type]}</span>
					</div>

					{[SurveyQuestionType.SINGLE_CHOICE, SurveyQuestionType.MULTI_CHOICE].includes(question.type) &&
						question.options && (
							<div className="flex flex-column gap-1 justify-content-start align-items-start">
								<span className="font-semibold">{t('programPanel.feedback.options')}:</span>
								<ArrowList>
									{question.options
										.slice(0, showAllOptions ? question.options.length : OPTIONS_LIMIT)
										.map((option) => (
											<li key={option.id}>{option.text}</li>
										))}
								</ArrowList>
								{question.options.length > OPTIONS_LIMIT && (
									<Button
										variant="primary-text"
										variantSize="sm"
										label={showAllOptions ? t('actions.showLess') : t('actions.showMore')}
										onClick={() => setShowAllOptions(!showAllOptions)}
									/>
								)}
							</div>
						)}

					{question.label && (
						<div className="flex flex-row gap-2">
							<span className="font-semibold">{t('programPanel.feedback.label')}:</span>
							<span>{question.label}</span>
						</div>
					)}
				</div>
			</BoxSection>
		</Box>
	);
});
